/**
 * The rules the tour list is built from, with no React and no DOM in them.
 *
 * Everything here works on the date *string* Bandsintown sends —
 * `"2026-04-01T20:00:00"` — and never on a `Date`. That is deliberate. The API
 * sends no timezone, so the string means "eight in the evening, at the venue".
 * Handing it to `new Date()` makes the runtime guess a zone: a build machine in
 * UTC and a browser in Stockholm then disagree by two hours, which is enough to
 * print the wrong day for anything near midnight and to drop a show from the
 * list a day early. Slicing the string cannot do that.
 */

export type TourEvent = {
  id: string;
  /** Venue-local, no timezone: `"2026-04-01T20:00:00"`. */
  datetime: string;
  venue: { name: string; city: string; country: string };
  url: string;
  soldOut: boolean;
  offers: { type: string; url: string; status: string }[];
};

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** The `YYYY-MM-DD` half of an API datetime, which is all the sorting needs. */
export function eventDay(datetime: string): string {
  return datetime.slice(0, 10);
}

/** `"2026-04-01T20:00:00"` -> `"01 Apr"`. Empty for anything unparseable. */
export function formatDay(datetime: string): string {
  const day = datetime.slice(8, 10);
  const month = Number(datetime.slice(5, 7));
  if (day.length !== 2 || !(month >= 1 && month <= 12)) return "";
  return `${day} ${MONTHS[month - 1]}`;
}

/** `"2026-04-01T20:00:00"` -> `"2026"`. Empty for anything unparseable. */
export function eventYear(datetime: string): string {
  const year = datetime.slice(0, 4);
  return /^\d{4}$/.test(year) ? year : "";
}

/**
 * Today as `YYYY-MM-DD` in the viewer's own timezone.
 *
 * Built from the local getters rather than `toISOString()`, which converts to
 * UTC first and so reports yesterday for anyone east of Greenwich late at night.
 */
export function today(now: Date = new Date()): string {
  const month = `${now.getMonth() + 1}`.padStart(2, "0");
  const day = `${now.getDate()}`.padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
}

/**
 * The events still to come, soonest first.
 *
 * Compared by day, not by instant: a show that started an hour ago is still
 * tonight's show, and dropping it off the page while the room is full would be
 * wrong. It leaves the list when the date does.
 */
export function upcoming(events: TourEvent[], from: string = today()): TourEvent[] {
  return events
    .filter((event) => eventDay(event.datetime) >= from)
    .sort((a, b) => a.datetime.localeCompare(b.datetime));
}

/**
 * The year for the panel header, or `null` when it would mislead.
 *
 * The header carries one year, so it can only be shown when every date shares
 * it. A list spanning a new year gets no year rather than a wrong one.
 */
export function headerYear(events: TourEvent[]): string | null {
  const years = new Set(events.map((event) => eventYear(event.datetime)).filter(Boolean));
  return years.size === 1 ? ([...years][0] ?? null) : null;
}

/**
 * The link behind "Tickets": the event's own page, shown only when the event has
 * a ticket offer and is not sold out. `null` means no button.
 */
export function ticketUrl(event: TourEvent): string | null {
  if (event.soldOut) return null;
  if (!event.offers.some((o) => o.url)) return null;
  return event.url || null;
}

/**
 * The "request a show" flow (ticket 037).
 *
 * The **path** is the mechanism, not the query. The old link was
 * `/a/15525238?trigger=play_my_city` — the artist page with a param that opens
 * nothing, so the button went exactly where "Follow me" goes. The real flow
 * lives at `/artist-subscribe/<id>` with `play-my-city=true`.
 *
 * Bandsintown's own logged-in page also emits `utm_*`, `spn` and a 64-character
 * `signature`. Those are left off deliberately: a signature that long is either
 * session-bound or something they can rotate out from under us, and a hardcoded
 * one would break silently on their schedule. If the flow ever stops opening
 * logged out, ticket 037 lists the fallbacks in order.
 *
 * `app_id` here is the widget's public domain-form id, not our REST key. The key
 * is the 32-character secret in `BIT_APP_ID` and only `scripts/fetch-events.ts`
 * ever sees it; `tourDates.test.ts` holds the line that it stays out of `src/`.
 */
export const REQUEST_A_SHOW_URL =
  "https://www.bandsintown.com/artist-subscribe/15525238?play-my-city=true&app_id=js_jadujoel.github.io&came_from=242";
